import { accountProgression, awardOfflineFightXp } from '../auth.js';

export const XP_PER_LEVEL = 1000;
export const XP_KILL = 100;
export const XP_HEADSHOT = 150;
const STORAGE_KEY = 'rift:progression:v1';

export function levelForXp(xp) {
  return Math.floor(Math.max(0, Number(xp) || 0) / XP_PER_LEVEL) + 1;
}

export function readLocalXp() {
  try {
    const value = JSON.parse(globalThis.localStorage?.getItem(STORAGE_KEY) ?? 'null');
    return Math.max(0, Math.floor(Number(value?.xp) || 0));
  } catch {
    return 0;
  }
}

export function writeLocalXp(xp) {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify({ xp: Math.max(0, Math.floor(xp)) }));
  } catch {
    // Private browsing can disable storage. Progress still lasts for this run.
  }
}

/**
 * Small progression component owned by PlayerSystem. Online PvP rewards are
 * authoritative in the game server; offline fights apply instantly here and
 * are mirrored to the signed-in account when the progression API is present.
 */
export class PlayerProgression {
  constructor(player) {
    this.player = player;
    this.xp = readLocalXp();
    this.authenticated = false;
    this._syncToken = 0;
    this._state = { xp: 0, level: 1, current: 0, next: XP_PER_LEVEL, progress: 0, authenticated: false };
    this._publish();
  }

  attach(ctx) {
    this.ctx = ctx;
    this._publish();
    this.refreshAccount();
  }

  snapshot() {
    const s = this._state;
    s.xp = this.xp;
    s.level = levelForXp(this.xp);
    s.current = this.xp % XP_PER_LEVEL;
    s.next = XP_PER_LEVEL;
    s.progress = s.current / XP_PER_LEVEL;
    s.authenticated = this.authenticated;
    return s;
  }

  setAuthoritative(xp, authenticated = this.authenticated) {
    const next = Math.max(0, Math.floor(Number(xp) || 0));
    this.authenticated = !!authenticated;
    if (next === this.xp) {
      this._publish();
      return this.snapshot();
    }
    this.xp = next;
    writeLocalXp(this.xp);
    this._publish();
    return this.snapshot();
  }

  async refreshAccount() {
    const token = ++this._syncToken;
    try {
      const data = await accountProgression();
      if (token !== this._syncToken) return this.snapshot();
      if (data.signedIn) return this.setAuthoritative(data.xp, true);
      this.authenticated = false;
      this._publish();
    } catch {
      // Local/dev builds intentionally have no service. Local XP still works.
    }
    return this.snapshot();
  }

  awardFight({ headshot = false, online = false } = {}) {
    const amount = headshot ? XP_HEADSHOT : XP_KILL;
    // PvP arrives again in a server roster update, which is the authority.
    // Offline has no socket, so it advances immediately and then reconciles.
    if (online) return amount;
    this.xp += amount;
    writeLocalXp(this.xp);
    this._publish(amount);
    awardOfflineFightXp(headshot)
      .then((data) => {
        if (data.signedIn) this.setAuthoritative(Math.max(this.xp, Number(data.xp) || 0), true);
      })
      .catch(() => {});
    return amount;
  }

  _publish(gained = 0) {
    const s = this.snapshot();
    this.player.xp = s.xp;
    this.player.level = s.level;
    this.ctx?.events?.emit('progression:changed', { ...s, gained });
  }
}
